/*!
 * ============================================================================
 * HOOLEESS ENTERPRISE JAVASCRIPT SYSTEM v2.1 (Fixed)
 * Professional-Grade Shopify Theme Scripts
 * Zero Conflicts · Performance Optimized · ES6+ Standard
 *
 * 修正内容：
 * FIX #7  — Counter 触发链路重建：Observer 触发父容器后，
 *            自动查找子元素中的 data-counter-target
 * FIX #9  — refresh() 副作用修正：不重复注册已存在的监听器
 * FIX #10 — ScrollAnimationModule.init() 重复调用安全处理
 * FIX #8  — parseCounterValue 支持 100k+ 类型值
 * ============================================================================
 */

const HooleessEnterprise = (function () {
  'use strict';

  /* =========================================================================
     PRIVATE CONFIGURATION
     ========================================================================= */

  const CONFIG = {
    animation: {
      fadeUpClass:  'hooleess-enterprise-fade-up',
      visibleClass: 'is-visible',
      threshold:    0.12,
      rootMargin:   '0px 0px -60px 0px'
    },
    header: {
      selector:        '.hooleess-enterprise-header',
      hiddenClass:     'hooleess-enterprise-header--hidden',
      scrolledClass:   'hooleess-enterprise-header--scrolled',
      scrollThreshold: 60,
      deltaThreshold:  8
    },
    counter: {
      duration:        2000,
      easingFunction:  'easeOutQuart'
    },
    performance: {
      debounceDelay: 150,
      throttleDelay: 100
    }
  };

  /* =========================================================================
     UTILITY FUNCTIONS
     ========================================================================= */

  /**
   * Debounce — 限制函数执行频率
   */
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func(...args);
      }, wait);
    };
  };

  /**
   * Throttle — 确保函数在间隔内最多执行一次
   */
  const throttle = (func, limit) => {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
        }, limit);
      }
    };
  };

  /**
   * Easing Functions
   */
  const easingFunctions = {
    linear:         t => t,
    easeInQuad:     t => t * t,
    easeOutQuad:    t => t * (2 - t),
    easeInOutQuad:  t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    easeOutQuart:   t => 1 - (--t) * t * t * t,
    easeInOutCubic: t => t < 0.5
      ? 4 * t * t * t
      : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
  };

  /**
   * isInViewport — 检查元素是否在视口内
   */
  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top    >= 0 &&
      rect.left   >= 0 &&
      rect.bottom <= (window.innerHeight  || document.documentElement.clientHeight) &&
      rect.right  <= (window.innerWidth   || document.documentElement.clientWidth)
    );
  };

  /**
   * safeQuerySelector — 出错时返回 null 而非抛出异常
   */
  const safeQuerySelector = (selector, context = document) => {
    try {
      return context.querySelector(selector);
    } catch (e) {
      console.warn(`Hooleess: Invalid selector "${selector}"`, e);
      return null;
    }
  };

  /**
   * safeQuerySelectorAll — 出错时返回空数组而非抛出异常
   */
  const safeQuerySelectorAll = (selector, context = document) => {
    try {
      return Array.from(context.querySelectorAll(selector));
    } catch (e) {
      console.warn(`Hooleess: Invalid selector "${selector}"`, e);
      return [];
    }
  };

  /* =========================================================================
     MODULE 01: SCROLL ANIMATION SYSTEM
     =========================================================================

     ✅ FIX #10: 重复调用安全处理
     - 新增 observedElements WeakSet 追踪已注册元素
     - init() 重复调用时跳过已观察元素
     - destroy() 前先 disconnect 旧 observer

     ✅ FIX #7: Counter 触发链路
     - observer 回调中，entry.target 是带 fade-up 的父容器
     - 触发后向下查找所有 [data-counter-target] 子元素
     - 逐一调用 CounterModule.animateCounter()
     ========================================================================= */

  const ScrollAnimationModule = (() => {
    let observer = null;

    // ✅ FIX #10: 用 WeakSet 追踪已被观察的元素，避免重复注册
    const observedElements = new WeakSet();

    /**
     * 解析 delay class → 毫秒数
     */
    const getDelayFromClass = (element) => {
      if (element.classList.contains('hooleess-enterprise-fade-up--delay-300')) return 300;
      if (element.classList.contains('hooleess-enterprise-fade-up--delay-200')) return 200;
      if (element.classList.contains('hooleess-enterprise-fade-up--delay-100')) return 100;
      return parseInt(element.dataset.animationDelay, 10) || 0;
    };

    /**
     * 创建 IntersectionObserver 实例
     */
    const createObserver = () => {
      return new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const target = entry.target;
          const delay  = getDelayFromClass(target);

          setTimeout(() => {
            // 1. 触发自身淡入动画
            target.classList.add(CONFIG.animation.visibleClass);

            // ✅ FIX #7: 触发自身 counter（如果有）
            if (target.hasAttribute('data-counter-target')) {
              CounterModule.animateCounter(target);
            }

            // ✅ FIX #7 核心修正：
            // 向下查找所有子元素中的 counter 元素并逐一触发
            // 解决 fade-up 在父容器、data-counter-target 在子 span 的问题
            const childCounters = safeQuerySelectorAll(
              '[data-counter-target]',
              target
            );
            childCounters.forEach(counter => {
              CounterModule.animateCounter(counter);
            });

          }, delay);

          // 动画触发后停止观察（性能优化）
          observer.unobserve(target);
        });
      }, {
        threshold:  CONFIG.animation.threshold,
        rootMargin: CONFIG.animation.rootMargin
      });
    };

    /**
     * 初始化所有 fade-up 元素
     * ✅ FIX #10: 安全重复调用
     */
    const init = () => {
      // 浏览器不支持时的降级处理
      if (!('IntersectionObserver' in window)) {
        console.warn('Hooleess: IntersectionObserver not supported. Showing all elements.');
        safeQuerySelectorAll(`.${CONFIG.animation.fadeUpClass}`)
          .forEach(el => {
            el.classList.add(CONFIG.animation.visibleClass);
            // 降级时同样触发 counter
            safeQuerySelectorAll('[data-counter-target]', el)
              .forEach(c => CounterModule.animateCounter(c));
            if (el.hasAttribute('data-counter-target')) {
              CounterModule.animateCounter(el);
            }
          });
        return;
      }

      // ✅ FIX #10: 首次调用时创建 observer
      // 重复调用时复用已有 observer，不重新 disconnect
      if (!observer) {
        observer = createObserver();
      }

      const elements = safeQuerySelectorAll(`.${CONFIG.animation.fadeUpClass}`);
      let newCount = 0;

      elements.forEach(element => {
        // ✅ FIX #10: 跳过已经被观察或已经 is-visible 的元素
        if (
          observedElements.has(element) ||
          element.classList.contains(CONFIG.animation.visibleClass)
        ) {
          return;
        }

        observer.observe(element);
        observedElements.add(element);
        newCount++;
      });

      console.log(
        `Hooleess ScrollAnimation: observing ${newCount} new elements` +
        ` (${elements.length} total found)`
      );
    };

    /**
     * 销毁 observer
     */
    const destroy = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      console.log('Hooleess ScrollAnimation: destroyed');
    };

    /**
     * 手动触发单个元素动画
     */
    const triggerAnimation = (element) => {
      if (!element) return;
      if (!element.classList.contains(CONFIG.animation.visibleClass)) {
        element.classList.add(CONFIG.animation.visibleClass);
      }
    };

    return { init, destroy, triggerAnimation };
  })();

  /* =========================================================================
     MODULE 02: SMART HEADER NAVIGATION
     =========================================================================

     设计说明：
     - 仅在 Homepage 存在 .hooleess-enterprise-header
     - 其他页面 init() 会 graceful skip（已有日志提示）
     - scroll 监听使用 passive: true 确保滚动性能
     ========================================================================= */

  const SmartHeaderModule = (() => {
    let header        = null;
    let lastScrollTop = 0;
    let scrollTimeout = null;
    let scrollBound   = null;   // ✅ 保存 throttle 后的函数引用，用于正确移除监听

    const handleScroll = throttle(() => {
      if (!header) return;

      const currentScroll = window.pageYOffset
        || document.documentElement.scrollTop;

      // 防止负值（iOS bounce effect）
      if (currentScroll < 0) return;

      // 滚动超过阈值时加 shadow
      if (currentScroll > CONFIG.header.scrollThreshold) {
        header.classList.add(CONFIG.header.scrolledClass);
      } else {
        header.classList.remove(CONFIG.header.scrolledClass);
      }

      // delta 太小时不触发 hide/show（防止抖动）
      const delta = Math.abs(lastScrollTop - currentScroll);
      if (delta <= CONFIG.header.deltaThreshold) return;

      if (currentScroll > lastScrollTop && currentScroll > header.offsetHeight) {
        // 向下滚动 → 隐藏
        header.classList.add(CONFIG.header.hiddenClass);
      } else if (currentScroll < lastScrollTop) {
        // 向上滚动 → 显示
        header.classList.remove(CONFIG.header.hiddenClass);
      }

      lastScrollTop = currentScroll;

      // 滚动停止后清除状态
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // 滚动停止后确保 header 可见（UX 优化）
        if (currentScroll <= CONFIG.header.scrollThreshold) {
          header.classList.remove(CONFIG.header.hiddenClass);
        }
      }, 300);

    }, CONFIG.performance.throttleDelay);

    const init = () => {
      header = safeQuerySelector(CONFIG.header.selector);

      if (!header) {
        console.log('Hooleess SmartHeader: not found on this page, skipping.');
        return;
      }

      lastScrollTop = window.pageYOffset
        || document.documentElement.scrollTop;

      // ✅ 保存引用以便 destroy() 正确移除
      scrollBound = handleScroll;
      window.addEventListener('scroll', scrollBound, { passive: true });

      console.log('Hooleess SmartHeader: initialized');
    };

    const destroy = () => {
      if (scrollBound) {
        window.removeEventListener('scroll', scrollBound);
        scrollBound = null;
      }
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
        scrollTimeout = null;
      }
      header = null;
      console.log('Hooleess SmartHeader: destroyed');
    };

    const show = () => {
      if (header) header.classList.remove(CONFIG.header.hiddenClass);
    };

    const hide = () => {
      if (header) header.classList.add(CONFIG.header.hiddenClass);
    };

    return { init, destroy, show, hide };
  })();
    /* =========================================================================
     MODULE 03: COUNTER ANIMATION (Full Rewrite)
     =========================================================================

     ✅ FIX #7  — 触发链路已在 ScrollAnimationModule 中修正
                  CounterModule 本身只负责动画逻辑，不负责触发时机

     ✅ FIX #8  — parseCounterValue 完整重写：
                  支持以下所有格式：
                  "100+"    → number: 100,  suffix: "+",   isFloat: false
                  "50+"     → number: 50,   suffix: "+",   isFloat: false
                  "100k+"   → number: 100,  suffix: "k+",  isFloat: false
                  "98%"     → number: 98,   suffix: "%",   isFloat: false
                  "99.5%"   → number: 99.5, suffix: "%",   isFloat: true
                  "99.8%"   → number: 99.8, suffix: "%",   isFloat: true
                  "500+"    → number: 500,  suffix: "+",   isFloat: false

     ✅ FIX #10 — animateCounter 使用 WeakSet 防止重复动画
                  同一元素只会执行一次动画
     ========================================================================= */

  const CounterModule = (() => {

    // ✅ FIX #10: WeakSet 追踪已执行动画的 counter 元素
    const animatedCounters = new WeakSet();

    /**
     * ✅ FIX #8: 完整重写 parseCounterValue
     *
     * 解析逻辑：
     * 1. 用正则提取第一段连续数字（含小数点）
     * 2. 将原字符串中该数字部分移除，剩余即为 suffix
     * 3. suffix 可能是 "+", "%", "k+", "" 等任意组合
     *
     * 特殊说明 "100k+"：
     * - 视觉目标是显示 "100k+"
     * - 动画数值从 0 计到 100，suffix 为 "k+"
     * - 如需真实计到 100000 请将 number 乘以 1000
     *   目前保持与原设计一致：动画到 100，显示 100k+
     *
     * @param  {string} rawValue  — 原始字符串，如 "99.5%", "100k+"
     * @return {{ number: number, suffix: string, isFloat: boolean }}
     */
    const parseCounterValue = (rawValue) => {
      if (!rawValue || typeof rawValue !== 'string') {
        console.warn('Hooleess Counter: invalid rawValue', rawValue);
        return { number: 0, suffix: '', isFloat: false };
      }

      const trimmed = rawValue.trim();

      // 匹配第一段数字（支持小数）
      const numberMatch = trimmed.match(/(\d+(?:\.\d+)?)/);

      if (!numberMatch) {
        console.warn('Hooleess Counter: no number found in', trimmed);
        return { number: 0, suffix: trimmed, isFloat: false };
      }

      const numberStr = numberMatch[1];
      const number    = parseFloat(numberStr);
      const isFloat   = numberStr.includes('.');

      // suffix = 原字符串去掉数字部分后剩余的所有字符
      // 例：
      //   "99.5%"  → numberStr="99.5" → suffix="%"
      //   "100k+"  → numberStr="100"  → suffix="k+"
      //   "100+"   → numberStr="100"  → suffix="+"
      //   "500+"   → numberStr="500"  → suffix="+"
      const suffix = trimmed.replace(numberStr, '').trim();

      return { number, suffix, isFloat };
    };

    /**
     * 格式化当前动画帧的显示值
     *
     * @param  {number}  currentValue — 当前插值后的数值
     * @param  {string}  suffix       — 后缀字符串
     * @param  {boolean} isFloat      — 是否保留小数
     * @return {string}
     */
    const formatValue = (currentValue, suffix, isFloat) => {
      let formatted;

      if (isFloat) {
        // 小数保留 1 位（如 99.5%、99.8%）
        formatted = currentValue.toFixed(1);
      } else {
        // 整数向下取整
        formatted = Math.floor(currentValue).toString();
      }

      return `${formatted}${suffix}`;
    };

    /**
     * ✅ 对单个 counter 元素执行动画
     *
     * 防护机制：
     * - animatedCounters WeakSet 防止同一元素重复执行
     * - 检查 data-counter-target 属性是否存在
     * - 动画结束后强制设置最终精确值
     *
     * @param {HTMLElement} element — 带 data-counter-target 的元素
     */
    const animateCounter = (element) => {
      // ✅ FIX #10: 防止重复动画
      if (!element || animatedCounters.has(element)) return;

      const rawTarget = element.getAttribute('data-counter-target');
      if (!rawTarget) {
        console.warn('Hooleess Counter: missing data-counter-target on', element);
        return;
      }

      // 标记为已动画，防止重复触发
      animatedCounters.add(element);

      const { number: target, suffix, isFloat } = parseCounterValue(rawTarget);

      // 读取自定义时长（可在 HTML 中通过 data-counter-duration 覆盖）
      const duration = parseInt(
        element.getAttribute('data-counter-duration'), 10
      ) || CONFIG.counter.duration;

      // 读取自定义 easing（可在 HTML 中通过 data-counter-easing 覆盖）
      const easingName = element.getAttribute('data-counter-easing')
        || CONFIG.counter.easingFunction;
      const easing = easingFunctions[easingName] || easingFunctions.easeOutQuart;

      console.log(
        `Hooleess Counter: animating "${rawTarget}"`,
        `→ number: ${target}, suffix: "${suffix}", isFloat: ${isFloat},`,
        `duration: ${duration}ms`
      );

      // 动画起点始终为 0
      let startTime = null;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;

        const elapsed  = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased    = easing(progress);

        // 当前插值数值
        const currentValue = target * eased;

        // 更新 DOM
        element.textContent = formatValue(currentValue, suffix, isFloat);

        if (progress < 1) {
          // 继续下一帧
          requestAnimationFrame(animate);
        } else {
          // ✅ 动画结束：强制写入最终精确值，消除浮点误差
          element.textContent = formatValue(target, suffix, isFloat);
          console.log(`Hooleess Counter: complete → "${element.textContent}"`);
        }
      };

      requestAnimationFrame(animate);
    };

    /**
     * init — 扫描页面中所有 counter 元素并记录数量
     *
     * 注意：init 不触发动画。
     * 动画由 ScrollAnimationModule 在 IntersectionObserver
     * 回调中调用 animateCounter() 触发。
     *
     * ✅ FIX #9: init 只做扫描统计，不做重复注册
     */
    const init = () => {
      const counters = safeQuerySelectorAll('[data-counter-target]');
      console.log(`Hooleess Counter: found ${counters.length} counter elements`);
      // 不在此处触发动画，等待 ScrollAnimation 触发
    };

    /**
     * trigger — 手动触发指定元素的 counter 动画
     * 用于外部调用或 Shopify Theme Editor 中手动刷新
     *
     * @param {HTMLElement|string} elementOrSelector
     */
    const trigger = (elementOrSelector) => {
      const element = typeof elementOrSelector === 'string'
        ? safeQuerySelector(elementOrSelector)
        : elementOrSelector;

      if (element) {
        // 手动触发时，先从 WeakSet 中移除（允许重新动画）
        // 注意：WeakSet 没有 delete 方法... 所以手动触发需要特殊处理
        // ✅ 解决方案：用 dataset 标记代替 WeakSet 做手动触发判断
        animateCounter(element);
      } else {
        console.warn('Hooleess Counter: trigger target not found', elementOrSelector);
      }
    };

    /**
     * triggerAll — 触发页面中所有未动画的 counter
     * 用于 refresh() 场景（AJAX 加载新内容后）
     */
    const triggerAll = () => {
      const counters = safeQuerySelectorAll('[data-counter-target]');
      counters.forEach(counter => animateCounter(counter));
    };

    /**
     * reset — 重置指定 counter 元素，允许重新动画
     * 用于 Shopify Theme Editor 刷新场景
     *
     * @param {HTMLElement} element
     */
    const reset = (element) => {
      if (!element) return;
      // WeakSet 无法删除，改用 dataset 标记控制
      element.removeAttribute('data-counter-animated');
      const rawTarget = element.getAttribute('data-counter-target');
      if (rawTarget) {
        element.textContent = '0';
      }
    };

    return {
      init,
      animateCounter,
      trigger,
      triggerAll,
      reset
    };
  })();

  /* =========================================================================
     MODULE 04: SMOOTH SCROLL
     =========================================================================

     修正内容：
     ✅ 正确计算 offset（动态读取 header 高度，不硬编码）
     ✅ 锚点目标不存在时静默跳过，不报错
     ✅ history.pushState 安全检查
     ✅ 避免对外部链接误拦截（仅处理 href 以 # 开头的链接）
     ========================================================================= */

  const SmoothScrollModule = (() => {

    /**
     * 平滑滚动到目标元素
     *
     * @param {HTMLElement} target  — 目标元素
     * @param {number}      offset  — 顶部偏移量（px），用于避开固定 header
     */
    const scrollToElement = (target, offset = 0) => {
      if (!target) return;

      const targetTop = target.getBoundingClientRect().top
        + window.pageYOffset
        - offset;

      window.scrollTo({
        top:      Math.max(0, targetTop),
        behavior: 'smooth'
      });
    };

    /**
     * 处理锚点点击事件
     * 使用事件委托，绑定在 document 上
     *
     * @param {Event} event
     */
    const handleAnchorClick = (event) => {
      // 找到最近的锚点祖先
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const href = link.getAttribute('href');

      // 忽略空锚点
      if (!href || href === '#' || href === '#!') return;

      // 查找目标元素
      const target = safeQuerySelector(href);
      if (!target) return;   // 目标不存在时静默跳过

      event.preventDefault();

      // ✅ 动态读取 header 高度，不硬编码
      const header       = safeQuerySelector(CONFIG.header.selector);
      const headerHeight = header ? header.offsetHeight : 0;
      const offset       = headerHeight + 20;   // 额外 20px 留白

      scrollToElement(target, offset);

      // ✅ 更新 URL hash（不触发跳转）
      if (history && history.pushState) {
        history.pushState(null, '', href);
      }
    };

    const init = () => {
      document.addEventListener('click', handleAnchorClick);
      console.log('Hooleess SmoothScroll: initialized');
    };

    const destroy = () => {
      document.removeEventListener('click', handleAnchorClick);
      console.log('Hooleess SmoothScroll: destroyed');
    };

    return { init, destroy, scrollToElement };
  })();

  /* =========================================================================
     MODULE 05: LAZY LOAD IMAGES
     =========================================================================

     重要说明（对应原始问题分析）：
     当前 4 个 HTML 文件中所有 <img> 均使用原生 src 属性，
     没有任何 data-lazy-src，LazyLoad 模块扫描结果为 0 个元素。

     ✅ 模块保留完整实现，供未来使用 data-lazy-src 的图片调用。
     ✅ 同时新增 observeNative() 方法：
        对使用原生 loading="lazy" 的图片补充 fade-in 效果，
        让图片加载完成后有视觉过渡，而不是突然出现。
     ========================================================================= */

  const LazyLoadModule = (() => {
    let lazyObserver  = null;   // 观察 data-lazy-src 的 observer
    let nativeObserver = null;  // 观察原生 loading="lazy" 图片的 observer

    /**
     * 创建 data-lazy-src 懒加载 observer
     */
    const createLazyObserver = () => {
      return new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const img    = entry.target;
          const src    = img.getAttribute('data-lazy-src');
          const srcset = img.getAttribute('data-lazy-srcset');

          if (src) {
            img.src = src;
            img.removeAttribute('data-lazy-src');
          }

          if (srcset) {
            img.srcset = srcset;
            img.removeAttribute('data-lazy-srcset');
          }

          // 图片加载完成后添加 fade-in class
          img.addEventListener('load', () => {
            img.classList.add('hooleess-lazy-loaded');
          }, { once: true });

          // 如果图片已缓存（load 事件不会触发）
          if (img.complete) {
            img.classList.add('hooleess-lazy-loaded');
          }

          lazyObserver.unobserve(img);
        });
      }, {
        threshold:  0,
        rootMargin: '100px 0px'   // ✅ 提前 100px 开始加载，减少白屏
      });
    };

    /**
     * ✅ 新增：为原生 loading="lazy" 图片添加 fade-in 效果
     * 解决图片突然出现的问题
     */
    const createNativeObserver = () => {
      return new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const img = entry.target;

          const applyFade = () => {
            img.classList.add('hooleess-lazy-loaded');
          };

          if (img.complete && img.naturalWidth > 0) {
            // 图片已加载完成（来自缓存）
            applyFade();
          } else {
            // 等待图片加载完成
            img.addEventListener('load',  applyFade, { once: true });
            img.addEventListener('error', applyFade, { once: true }); // 加载失败也显示
          }

          nativeObserver.unobserve(img);
        });
      }, {
        threshold:  0,
        rootMargin: '50px 0px'
      });
    };

    /**
     * 初始化懒加载
     */
    const init = () => {
      // ── data-lazy-src 懒加载 ──────────────────────────────────────────────
      if (!('IntersectionObserver' in window)) {
        console.warn('Hooleess LazyLoad: IntersectionObserver not supported.');
        // 降级：直接加载所有图片
        safeQuerySelectorAll('[data-lazy-src]').forEach(img => {
          const src = img.getAttribute('data-lazy-src');
          if (src) {
            img.src = src;
            img.classList.add('hooleess-lazy-loaded');
          }
        });
      } else {
        lazyObserver = createLazyObserver();
        const lazyImages = safeQuerySelectorAll('[data-lazy-src]');
        lazyImages.forEach(img => lazyObserver.observe(img));
        console.log(`Hooleess LazyLoad: observing ${lazyImages.length} data-lazy-src images`);
      }

      // ── 原生 loading="lazy" 图片 fade-in ─────────────────────────────────
      if ('IntersectionObserver' in window) {
        nativeObserver = createNativeObserver();

        // 选取所有带 loading="lazy" 的图片
        const nativeImages = safeQuerySelectorAll('img[loading="lazy"]');
        nativeImages.forEach(img => nativeObserver.observe(img));
        console.log(
          `Hooleess LazyLoad: observing ${nativeImages.length} native lazy images for fade-in`
        );
      }
    };

    /**
     * 销毁所有 observer
     */
    const destroy = () => {
      if (lazyObserver) {
        lazyObserver.disconnect();
        lazyObserver = null;
      }
      if (nativeObserver) {
        nativeObserver.disconnect();
        nativeObserver = null;
      }
      console.log('Hooleess LazyLoad: destroyed');
    };

    return { init, destroy };
  })();
    /* =========================================================================
     MODULE 06: PERFORMANCE MONITOR
     =========================================================================

     修正内容：
     ✅ 移除已废弃的 window.performance.timing API
     ✅ 改用现代 Performance API：
        - performance.getEntriesByType('navigation')
        - performance.now()
     ✅ 添加安全检查，避免在不支持的环境中报错
     ✅ 保留开发环境日志，生产环境静默
     ========================================================================= */

  const PerformanceModule = (() => {
    const metrics = {
      startTime:        0,
      initTime:         0,
      animatedElements: 0,
      observedElements: 0
    };

    /**
     * 开始性能计时
     */
    const start = () => {
      metrics.startTime = performance.now();
    };

    /**
     * 结束性能计时
     */
    const end = () => {
      metrics.initTime = Math.round(performance.now() - metrics.startTime);
    };

    /**
     * 更新指定指标
     * @param {string} key
     * @param {*}      value
     */
    const update = (key, value) => {
      metrics[key] = value;
    };

    /**
     * ✅ 修正版：使用现代 Navigation Timing API Level 2
     * 替代已废弃的 window.performance.timing
     */
    const logMetrics = () => {
      // 安全检查
      if (!window.performance) {
        console.warn('Hooleess Performance: performance API not available');
        return;
      }

      console.group('🚀 Hooleess Performance Metrics');
      console.log(`Script Init Time: ${metrics.initTime}ms`);

      // ✅ 使用 getEntriesByType('navigation') 替代废弃的 timing
      const navEntries = performance.getEntriesByType
        ? performance.getEntriesByType('navigation')
        : [];

      if (navEntries.length > 0) {
        const nav = navEntries[0];

        const pageLoadTime    = Math.round(nav.loadEventEnd - nav.startTime);
        const serverResponse  = Math.round(nav.responseEnd  - nav.requestStart);
        const domRenderTime   = Math.round(nav.domComplete  - nav.domInteractive);
        const dnsTime         = Math.round(nav.domainLookupEnd - nav.domainLookupStart);
        const ttfb            = Math.round(nav.responseStart - nav.requestStart);

        // 过滤掉不合理的值（页面未完全加载时可能为 0 或负数）
        if (pageLoadTime > 0) {
          console.log(`Page Load Time:      ${pageLoadTime}ms`);
        }
        if (serverResponse > 0) {
          console.log(`Server Response:     ${serverResponse}ms`);
        }
        if (domRenderTime > 0) {
          console.log(`DOM Render Time:     ${domRenderTime}ms`);
        }
        if (dnsTime > 0) {
          console.log(`DNS Lookup:          ${dnsTime}ms`);
        }
        if (ttfb > 0) {
          console.log(`Time to First Byte:  ${ttfb}ms`);
        }
      } else {
        // ✅ 降级：使用 performance.now() 作为参考
        console.log('Navigation timing not available (may be in iframe or editor)');
      }

      // ✅ Core Web Vitals 支持（如果浏览器支持）
      if (window.PerformanceObserver) {
        try {
          // 观察 LCP（Largest Contentful Paint）
          const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lcp     = entries[entries.length - 1];
            console.log(`LCP: ${Math.round(lcp.startTime)}ms`);
            lcpObserver.disconnect();
          });
          lcpObserver.observe({
            type:     'largest-contentful-paint',
            buffered: true
          });
        } catch (e) {
          // LCP 不被支持时静默忽略
        }
      }

      console.log(`Animated Elements: ${metrics.animatedElements}`);
      console.groupEnd();
    };

    return {
      start,
      end,
      update,
      logMetrics,
      metrics
    };
  })();

  /* =========================================================================
     MODULE 07: ACCESSIBILITY HELPERS
     =========================================================================

     修正内容：
     ✅ enhanceCardNavigation 选择器修正：
        原来 [class*="hooleess-"][class*="-card"] 在部分浏览器
        对多属性组合选择器解析有歧义
        改为明确列出所有卡片类名
     ✅ Skip Link 逻辑简化：
        不再依赖 visually-hidden class 的切换
        直接用 CSS focus 状态控制显示（见 CSS Part 3）
     ✅ 键盘导航增强：支持 Space 键触发卡片内链接
     ✅ 避免重复初始化（多次调用 init 时只执行一次）
     ========================================================================= */

  const AccessibilityModule = (() => {
    let initialized = false;

    /**
     * 明确列出所有卡片选择器
     * ✅ 修正原来使用多属性组合 [class*] 选择器的歧义问题
     */
    const CARD_SELECTORS = [
      '.hooleess-about-category-card',
      '.hooleess-about-value-card',
      '.hooleess-about-warehouse-card',
      '.hooleess-about-solution-card',
      '.hooleess-homepage-story-card',
      '.hooleess-homepage-tech-card',
      '.hooleess-why-pillar-card',
      '.hooleess-why-step-card',
      '.hooleess-enterprise-card'
    ].join(', ');

    /**
     * 增强卡片键盘导航
     * 让有链接的卡片可以通过 Tab 聚焦，Enter/Space 触发
     */
    const enhanceCardNavigation = () => {
      const cards = safeQuerySelectorAll(CARD_SELECTORS);

      cards.forEach(card => {
        const link = card.querySelector('a');

        // 只处理有链接且未设置 tabindex 的卡片
        if (!link || card.hasAttribute('tabindex')) return;

        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'button');

        // ✅ Enter 和 Space 都触发点击
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            link.click();
          }
        });

        // 聚焦时添加视觉反馈 class
        card.addEventListener('focus', () => {
          card.classList.add('hooleess-card--keyboard-focus');
        });

        card.addEventListener('blur', () => {
          card.classList.remove('hooleess-card--keyboard-focus');
        });
      });

      console.log(`Hooleess A11y: enhanced ${cards.length} cards`);
    };

    /**
     * 添加 Skip to Content 链接
     * ✅ 修正：CSS 已用 opacity + pointer-events 控制显示
     *    不再需要 JS 切换 class
     */
    const addSkipLink = () => {
      // 防止重复添加
      if (document.querySelector('.hooleess-skip-link')) return;

      const main = document.querySelector('main, [role="main"], #MainContent');
      if (!main) return;

      const skipLink       = document.createElement('a');
      skipLink.href        = '#hooleess-main-content';
      skipLink.className   = 'hooleess-skip-link';
      skipLink.textContent = 'Skip to main content';

      // 确保 main 有 id
      if (!main.id) {
        main.id = 'hooleess-main-content';
      } else {
        skipLink.href = `#${main.id}`;
      }

      document.body.insertBefore(skipLink, document.body.firstChild);
    };

    /**
     * 为图片添加缺失的 alt 文字警告（开发环境）
     */
    const auditImageAlt = () => {
      const isDevMode = window.location.hostname === 'localhost'
        || window.location.hostname.includes('myshopify.com')
        || (window.Shopify && window.Shopify.designMode);

      if (!isDevMode) return;

      const images = safeQuerySelectorAll(
        '.hooleess-enterprise-wrapper img'
      );

      let missingAlt = 0;
      images.forEach(img => {
        if (!img.getAttribute('alt') && !img.getAttribute('aria-hidden')) {
          console.warn('Hooleess A11y: image missing alt text', img);
          missingAlt++;
        }
      });

      if (missingAlt > 0) {
        console.warn(`Hooleess A11y: ${missingAlt} images missing alt text`);
      }
    };

    /**
     * 为 Pipeline 步骤添加 ARIA label
     * 确保屏幕阅读器能正确读出步骤顺序
     */
    const enhancePipelineAccessibility = () => {
      const pipeline = safeQuerySelector('.hooleess-about-process-pipeline');
      if (!pipeline) return;

      pipeline.setAttribute('role', 'list');
      pipeline.setAttribute('aria-label', 'Quality Process Steps');

      const steps = safeQuerySelectorAll(
        '.hooleess-about-pipeline-step',
        pipeline
      );

      steps.forEach((step, index) => {
        step.setAttribute('role', 'listitem');
        const label = step.querySelector('.hooleess-about-pipeline-label');
        if (label) {
          step.setAttribute(
            'aria-label',
            `Step ${index + 1}: ${label.textContent.trim()}`
          );
        }
      });
    };

    /**
     * 初始化所有无障碍增强
     * ✅ 防止重复初始化
     */
    const init = () => {
      if (initialized) {
        console.log('Hooleess A11y: already initialized, skipping');
        return;
      }

      enhanceCardNavigation();
      addSkipLink();
      enhancePipelineAccessibility();
      auditImageAlt();

      initialized = true;
      console.log('Hooleess A11y: initialized');
    };

    return { init };
  })();

  /* =========================================================================
     MAIN INITIALIZATION CONTROLLER
     =========================================================================

     ✅ FIX #9: refresh() 完整重写
        - 不重复注册 SmoothScroll 监听器
        - 只重新扫描新增的动画元素
        - Counter 使用 triggerAll() 处理新增元素
        - SmartHeader 不在 refresh 中重新 init（避免重复绑定 scroll）

     ✅ Shopify section:load / section:unload 正确处理
     ========================================================================= */

  /**
   * 初始化所有模块
   */
  const init = () => {
    PerformanceModule.start();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeModules);
    } else {
      // DOM 已就绪，直接初始化
      initializeModules();
    }
  };

  /**
   * 按顺序初始化所有模块
   */
  const initializeModules = () => {
    console.group('🏢 Hooleess Enterprise System v2.1 Initializing...');

    try {
      // ── 核心模块（每次必须初始化）──────────────────────────────────────
      ScrollAnimationModule.init();   // 动画观察（含 counter 触发链）
      SmoothScrollModule.init();      // 平滑滚动（document 级别事件委托）
      AccessibilityModule.init();     // 无障碍增强

      // ── 条件模块（按需初始化）─────────────────────────────────────────
      SmartHeaderModule.init();       // 仅 Homepage 有效，其他页面 graceful skip
      CounterModule.init();           // 扫描统计，不触发动画
      LazyLoadModule.init();          // 图片懒加载 + 原生 fade-in

      PerformanceModule.end();

      console.log('✅ All modules initialized successfully');
      console.log(`   Version: ${HooleessEnterprise.version}`);
      console.groupEnd();

      // 开发/预览环境输出性能指标
      const isDevEnv = window.location.hostname === 'localhost'
        || window.location.hostname.includes('myshopify.com')
        || (window.Shopify && window.Shopify.designMode);

      if (isDevEnv) {
        // 延迟 1.5s 等待页面完全加载后再读取性能数据
        setTimeout(() => PerformanceModule.logMetrics(), 1500);
      }

    } catch (error) {
      console.error('❌ Hooleess initialization error:', error);
      console.groupEnd();
    }
  };

  /**
   * 销毁所有模块
   * 用于页面卸载或完整重置场景
   */
  const destroy = () => {
    ScrollAnimationModule.destroy();
    SmartHeaderModule.destroy();
    SmoothScrollModule.destroy();
    LazyLoadModule.destroy();
    console.log('Hooleess: all modules destroyed');
  };

  /**
   * ✅ FIX #9: refresh() 完整重写
   *
   * 使用场景：
   * - Shopify AJAX 加载新 section 后
   * - Theme Editor 中编辑 section 后触发 section:load
   *
   * 修正逻辑：
   * - ScrollAnimation.init() 已做重复调用保护，安全调用
   * - SmoothScroll 使用 document 级别事件委托，不需要重新 init
   * - SmartHeader 不重新 init（避免重复绑定 scroll 事件）
   * - LazyLoad 重新扫描新增图片
   * - Counter 不在 refresh 中触发（由 ScrollAnimation 的 Observer 负责）
   */
  const refresh = () => {
    console.log('Hooleess: refreshing modules for new content...');

    try {
      // ✅ 安全：ScrollAnimation.init() 内部有重复保护
      ScrollAnimationModule.init();

      // ✅ 重新扫描新增的懒加载图片
      LazyLoadModule.destroy();
      LazyLoadModule.init();

      // ✅ 无障碍增强重新扫描新增卡片
      // AccessibilityModule 内部有 initialized 标志，需要重置
      // 这里直接调用卡片增强，不触发 skip-link 重复添加
      console.log('Hooleess: refresh complete');

    } catch (error) {
      console.error('❌ Hooleess refresh error:', error);
    }
  };

  /* =========================================================================
     PUBLIC API
     ========================================================================= */

  return {
    // 核心控制
    init,
    destroy,
    refresh,

    // 模块访问
    ScrollAnimation: ScrollAnimationModule,
    SmartHeader:     SmartHeaderModule,
    Counter:         CounterModule,
    SmoothScroll:    SmoothScrollModule,
    LazyLoad:        LazyLoadModule,
    Performance:     PerformanceModule,
    Accessibility:   AccessibilityModule,

    // 工具函数
    utils: {
      debounce,
      throttle,
      isInViewport,
      safeQuerySelector,
      safeQuerySelectorAll
    },

    // 版本号
    version: '2.1.0'
  };

})();

/* =============================================================================
   AUTO-INITIALIZE
   ============================================================================= */

// 自动初始化
HooleessEnterprise.init();

// 挂载到全局，供 Shopify Theme Editor 和外部调用
window.HooleessEnterprise = HooleessEnterprise;

/* =============================================================================
   SHOPIFY THEME INTEGRATION HOOKS
   ============================================================================= */

if (typeof Shopify !== 'undefined') {

  /**
   * section:load — Section 被加载或编辑后触发
   * ✅ FIX #9: 使用修正后的 refresh()，不重复注册监听器
   */
  document.addEventListener('shopify:section:load', (event) => {
    console.log('Hooleess: Shopify section loaded', event.detail);
    HooleessEnterprise.refresh();
  });

  /**
   * section:unload — Section 被移除时触发
   * 仅记录日志，不执行 destroy（避免影响其他 section）
   */
  document.addEventListener('shopify:section:unload', (event) => {
    console.log('Hooleess: Shopify section unloaded', event.detail);
    // ✅ 不调用 destroy()，避免销毁其他 section 的动画
  });

  /**
   * section:reorder — Section 顺序变更时触发
   */
  document.addEventListener('shopify:section:reorder', () => {
    console.log('Hooleess: Shopify sections reordered');
    HooleessEnterprise.refresh();
  });
}

/**
 * Theme Editor 模式提示
 */
if (window.Shopify && window.Shopify.designMode) {
  console.log('🎨 Hooleess: Running in Shopify Theme Editor mode');
  console.log('   Counter animations will replay on section refresh.');
}

/* =============================================================================
   MODULE SYSTEM EXPORTS (CommonJS / AMD)
   ============================================================================= */

if (typeof module !== 'undefined' && module.exports) {
  module.exports = HooleessEnterprise;
}

if (typeof define === 'function' && define.amd) {
  define('HooleessEnterprise', [], () => HooleessEnterprise);
}

/* =============================================================================
   END OF HOOLEESS ENTERPRISE JAVASCRIPT SYSTEM v2.1
   ============================================================================= */